import { useState, useEffect } from "react";
import { toast } from "sonner";
import { funeralStoreService } from "../../services/funeralStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Layers, Upload, X } from "lucide-react";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { getImageUrl } from "../../utils/imageUtils";
import { cn } from "@/lib/utils";
import api from "../../services/api";

export default function AdminFuneralStoreCollections() {
    const [collections, setCollections] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sheetOpen, setSheetOpen] = useState(false);
    const [editingCollection, setEditingCollection] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        cover_image: "",
        products: [],
        is_active: true,
        is_featured: false
    });

    useEffect(() => {
        fetchCollections();
        fetchProducts();
    }, [page, limit, searchTerm]);

    // Reset to page 1 when search term changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const fetchCollections = async () => {
        try {
            setLoading(true);
            const response = await funeralStoreService.admin.collections.getAll({ 
                page, 
                limit,
                keyword: searchTerm 
            });
            setCollections(response.data || []);
            setTotalItems(response.totalItems || 0);
            setTotalPages(response.totalPages || 1);
        } catch (error) {
            toast.error("Failed to fetch collections");
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await funeralStoreService.admin.products.getAll({ limit: 1000 });
            const productsList = response.data || [];
            setProducts(productsList.map(p => ({ label: p.name, value: p.product_id })));
        } catch (error) {
            console.error("Fetch products error:", error);
        }
    };

    const handleEdit = (collection) => {
        setEditingCollection(collection);
        setFormData({
            name: collection.name,
            description: collection.description || "",
            cover_image: collection.cover_image || "",
            products: collection.products || [],
            is_active: collection.is_active,
            is_featured: collection.is_featured || false
        });
        setSheetOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await funeralStoreService.admin.collections.delete(id);
            toast.success("Collection deleted");
            fetchCollections();
        } catch (error) {
            toast.error("Failed to delete collection");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCollection) {
                await funeralStoreService.admin.collections.update(editingCollection.collection_id, formData);
                toast.success("Collection updated");
            } else {
                await funeralStoreService.admin.collections.create(formData);
                toast.success("Collection created");
            }
            setSheetOpen(false);
            resetForm();
            fetchCollections();
        } catch (error) {
            toast.error("Failed to save collection");
        }
    };

    const resetForm = () => {
        setEditingCollection(null);
        setFormData({
            name: "",
            description: "",
            cover_image: "",
            products: [],
            is_active: true,
            is_featured: false
        });
    };

    // Removed local filtering logic - now handled server-side

    // Removed component-level loading guard - now handled localized in the table

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">Collections</h2>
                    <p className="text-slate-500">Organize products into collections</p>
                </div>
                <Button onClick={() => { resetForm(); setSheetOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Collection
                </Button>
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                        <SheetHeader>
                            <SheetTitle>{editingCollection ? "Edit Collection" : "New Collection"}</SheetTitle>
                        </SheetHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                            <div className="space-y-2">
                                <Label>Collection Name *</Label>
                                <Input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Premium Caskets, Vases" />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} />
                            </div>
                            <div className="space-y-2">
                                <Label>Select Products</Label>
                                <MultiSelect
                                    options={products}
                                    selected={formData.products}
                                    onChange={s => setFormData({ ...formData, products: s })}
                                    placeholder="Choose products..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Cover Image</Label>
                                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;

                                            const fd = new FormData();
                                            fd.append('image', file);

                                            try {
                                                const res = await api.post(`/upload/collection-image`, fd, {
                                                    headers: { 'Content-Type': 'multipart/form-data' }
                                                });
                                                setFormData({ ...formData, cover_image: res.data.path });
                                                toast.success("Image uploaded!");
                                            } catch (err) {
                                                toast.error("Upload failed");
                                            }
                                        }}
                                    />
                                    {formData.cover_image ? (
                                        <div className="relative group aspect-video rounded-lg overflow-hidden border border-slate-200">
                                            <img src={getImageUrl(formData.cover_image)} alt="Cover" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                <Upload className="w-8 h-8 text-white" />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFormData({ ...formData, cover_image: "" });
                                                }}
                                                className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full text-slate-600 hover:text-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-2 text-slate-500 py-4">
                                            <Upload className="w-8 h-8" />
                                            <p className="text-sm font-medium">Click to upload cover image</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center space-x-6 pt-2">
                                <div className="flex items-center space-x-2">
                                    <Checkbox id="c_active" checked={formData.is_active} onCheckedChange={c => setFormData({ ...formData, is_active: c })} />
                                    <Label htmlFor="c_active" className="cursor-pointer">Active</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox id="c_featured" checked={formData.is_featured} onCheckedChange={c => setFormData({ ...formData, is_featured: c })} />
                                    <Label htmlFor="c_featured" className="cursor-pointer">Featured</Label>
                                </div>
                            </div>
                            <Button type="submit" className="w-full bg-indigo-600 mt-4">
                                {editingCollection ? "Update Collection" : "Create Collection"}
                            </Button>
                        </form>
                    </SheetContent>
                </Sheet>
            </div>

            <div className="flex items-center gap-2 max-w-sm">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search collections..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
                </div>
            </div>

            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="w-[80px]">Image</TableHead>
                            <TableHead>Collection Name</TableHead>
                            <TableHead>Products</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-12">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-slate-500 text-sm font-medium">Loading collections...</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : collections.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">No collections found</TableCell></TableRow>
                        ) : (
                            collections.map(col => (
                                <TableRow key={col.collection_id} className={cn("transition-opacity", !col.is_active && "opacity-60 grayscale-[0.3]")}>
                                    <TableCell>
                                        {col.cover_image ? (
                                            <img src={getImageUrl(col.cover_image)} alt="" className="w-10 h-10 object-cover rounded" />
                                        ) : (
                                            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                                                <Layers className="w-5 h-5" />
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="font-medium text-slate-900">
                                        {col.name}
                                        {col.is_featured && <Badge variant="secondary" className="ml-2 bg-amber-50 text-amber-600 border-amber-100">Featured</Badge>}
                                    </TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                            {col.product_count || 0} Products
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`px-2 py-1 rounded-full text-xs ${col.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {col.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(col)}><Pencil className="w-4 h-4" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(col.collection_id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {totalPages > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 border-t gap-4 bg-white rounded-b-xl border-x border-b">
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                        <div>
                            Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-slate-600">Rows per page:</span>
                            <Select
                                value={limit.toString()}
                                onValueChange={(val) => {
                                    setLimit(Number(val));
                                    setPage(1);
                                }}
                            >
                                <SelectTrigger className="h-8 w-[70px]">
                                    <SelectValue placeholder={limit.toString()} />
                                </SelectTrigger>
                                <SelectContent side="top">
                                    {[10, 15, 25, 50, 100].map((pageSize) => (
                                        <SelectItem key={pageSize} value={pageSize.toString()}>
                                            {pageSize}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            Previous
                        </Button>

                        <div className="flex items-center mx-2 space-x-1">
                            {Array.from({ length: totalPages }).map((_, i) => {
                                const pageNum = i + 1;
                                if (
                                    pageNum === 1 ||
                                    pageNum === totalPages ||
                                    (pageNum >= page - 1 && pageNum <= page + 1)
                                ) {
                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={page === pageNum ? "default" : "outline"}
                                            size="sm"
                                            className={cn(
                                                "h-8 w-8 p-0",
                                                page === pageNum ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100"
                                            )}
                                            onClick={() => setPage(pageNum)}
                                        >
                                            {pageNum}
                                        </Button>
                                    );
                                }
                                if (pageNum === page - 2 || pageNum === page + 2) {
                                    return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                }
                                return null;
                            })}
                        </div>

                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
